import { UserOverviewSchema } from '@racemap/sdk/schema/user';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { isValidEmail } from '@racemap/utilities/functions/validation';
import { castDraft, produce } from 'immer';

import type { StoreApi } from 'zustand';
import type { DraftState, State } from '../../reducers';
import { fetchUsers } from '../trackers_helper';
import type { TrackersActions } from '../trackers_reducers';

const apiClient = RacemapAPIClient.fromWindowLocation();

export const createFilterActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Pick<
  TrackersActions,
  'setFilterSet' | 'setSearchValue' | 'setUserSearchValue' | 'resetFilterSelection'
> => ({
  setFilterSet: (newSet) => {
    const currentSet = get().trackers.filterSet;
    const newEntries = Array.from(newSet).filter((f) => !currentSet.has(f));
    const removedEntries = Array.from(currentSet).filter((f) => !newSet.has(f));

    set(
      produce((s: DraftState) => {
        s.trackers.filterSet = castDraft(newSet);
      }),
    );

    if (
      newEntries.some((f) => f.startsWith('/users/')) ||
      removedEntries.some((f) => f.startsWith('/users/'))
    ) {
      set(
        produce((s: DraftState) => {
          s.trackers.lastTrackerLoadedAt = undefined;
        }),
      );
      get().trackers.actions.loadData();
    }
  },

  setSearchValue: async (newSearchValue) => {
    const searchIsEmail = isValidEmail(newSearchValue);
    const { data: users } = searchIsEmail
      ? await apiClient.getUsers({
          search: newSearchValue,
        })
      : { data: [] };

    if (users != null && users.length > 0 && users[0].id != null) {
      const userId = users[0].id;

      set(
        produce((s: DraftState) => {
          s.trackers.users.set(
            userId,
            UserOverviewSchema.parse({
              id: userId,
              email: users[0].email,
              name: users[0].name,
            }),
          );
          s.trackers.filterSet.forEach((f) => {
            if (f.startsWith('/users/')) s.trackers.filterSet.delete(f);
          });
          s.trackers.filterSet.add(`/users/${userId}/owner`);
          s.trackers.filterSet.add(`/users/${userId}/editor`);
          s.trackers.filterSet.add(`/users/${userId}/borrower`);
          s.trackers.searchValue = '';
          s.trackers.lastTrackerLoadedAt = undefined;
        }),
      );
      await get().trackers.actions.loadData();
      return;
    }

    set(
      produce((s: DraftState) => {
        s.trackers.searchValue = newSearchValue;
      }),
    );
  },

  setUserSearchValue: async (newSearchValue: string) => {
    set(
      produce((s: DraftState) => {
        s.trackers.userSearchValue = newSearchValue;
      }),
    );

    const newUsers = await fetchUsers(get().trackers.userSearchValue, get().trackers.filterSet);

    set(
      produce((s: DraftState) => {
        s.trackers.users = newUsers;
      }),
    );
  },

  resetFilterSelection: async () => {
    const hadUserFilters = Array.from(get().trackers.filterSet).some((f) =>
      f.startsWith('/users/'),
    );
    set(
      produce((s: DraftState) => {
        s.trackers.searchValue = '';
        s.trackers.filterSet = new Set();
        if (hadUserFilters) {
          s.trackers.lastTrackerLoadedAt = undefined;
        }
      }),
    );
    await get().trackers.actions.loadData();
  },
});
